import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { services } from '../data/mockData';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const service = services.find(s => s.id === serviceId);

  if (!service || !service.fullDesc) {
    return (
      <div className="container section-padding">
        <h1 className="hero-medium">Service not found</h1>
        <Link to="/services" className="btn-secondary" style={{ marginTop: '24px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <ArrowLeft size={16} />
          Back to Services
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section style={{ 
        background: 'var(--bg-secondary)',
        padding: '80px 0',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div className="container">
          <Link 
            to="/services"
            className="btn-secondary"
            style={{ 
              marginBottom: '24px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <ArrowLeft size={16} />
            Back to Services
          </Link>
          <h1 className="hero-large" style={{ marginBottom: '16px' }}>
            {service.title}
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            {service.shortDesc}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="section-padding">
        <div className="container">
          <div className="grid-two-column">
            <div>
              <img 
                src={service.image}
                alt={service.title}
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '0px'
                }}
              />
            </div>
            
            <div>
              <h2 className="heading-2" style={{ marginBottom: '16px' }}>
                About This Treatment
              </h2>
              <p className="body-large" style={{ 
                marginBottom: '32px',
                lineHeight: '1.8',
                color: 'var(--text-primary)'
              }}>
                {service.fullDesc}
              </p>

              <Link to="/book-appointment" className="btn-primary">
                Book Appointment
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      {service.benefits && (
        <section style={{ background: 'var(--bg-secondary)' }} className="section-padding">
          <div className="container">
            <h2 className="hero-medium" style={{ marginBottom: '48px', textAlign: 'center' }}>
              Benefits
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px',
              maxWidth: '1000px',
              margin: '0 auto'
            }}>
              {service.benefits.map((benefit, index) => (
                <div 
                  key={index}
                  style={{
                    display: 'flex',
                    gap: '16px',
                    padding: '24px',
                    background: 'var(--bg-primary)',
                    borderRadius: '0px',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <CheckCircle 
                    size={20} 
                    style={{ 
                      color: 'var(--text-primary)',
                      flexShrink: 0,
                      marginTop: '2px'
                    }} 
                  />
                  <span className="body-regular">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Procedure Section */}
      {service.procedure && (
        <section className="section-padding">
          <div className="container">
            <h2 className="hero-medium" style={{ marginBottom: '48px', textAlign: 'center' }}>
              Treatment Procedure
            </h2>
            <div style={{
              maxWidth: '800px',
              margin: '0 auto'
            }}>
              {service.procedure.map((step, index) => (
                <div 
                  key={index}
                  style={{
                    display: 'flex',
                    gap: '24px',
                    marginBottom: '32px',
                    paddingBottom: '32px',
                    borderBottom: index < service.procedure.length - 1 ? '1px solid var(--border-light)' : 'none'
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    flexShrink: 0,
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-medium)',
                    borderRadius: '0px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'Suisse Medium, sans-serif',
                    fontWeight: 700,
                    fontSize: '18px'
                  }}>
                    {index + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p className="body-large">{step}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Who It's For Section */}
      {service.whoItsFor && (
        <section style={{ background: 'var(--bg-secondary)' }} className="section-padding">
          <div className="container">
            <h2 className="hero-medium" style={{ marginBottom: '48px', textAlign: 'center' }}>
              Who Is This Treatment For?
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '24px',
              maxWidth: '900px',
              margin: '0 auto'
            }}>
              {service.whoItsFor.map((item, index) => (
                <div 
                  key={index}
                  style={{
                    padding: '24px',
                    background: 'var(--bg-primary)',
                    borderRadius: '0px',
                    border: '1px solid var(--border-light)',
                    textAlign: 'center'
                  }}
                >
                  <p className="body-regular">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us Section */}
      <section className="section-padding">
        <div className="container">
          <div style={{
            background: 'var(--interactive-base)',
            color: 'var(--bg-primary)',
            padding: '64px 48px',
            textAlign: 'center',
            borderRadius: '0px'
          }}>
            <h2 className="hero-medium" style={{ marginBottom: '24px', color: 'var(--bg-primary)' }}>
              Why Choose The Eternal Care for {service.title}?
            </h2>
            <p className="body-large" style={{ 
              marginBottom: '32px',
              maxWidth: '700px',
              margin: '0 auto 32px',
              opacity: 0.9
            }}>
              Experience painless procedures with advanced technology, international sterilization 
              standards, and compassionate care from Dr. Parmila Mahala and our expert team.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link 
                to="/book-appointment" 
                className="btn-primary"
                style={{
                  background: 'var(--bg-primary)',
                  color: 'var(--interactive-base)',
                  border: '1px solid var(--bg-primary)'
                }}
              >
                Book Appointment
              </Link>
              <Link 
                to="/doctor"
                className="btn-primary"
                style={{
                  background: 'transparent',
                  color: 'var(--bg-primary)',
                  border: '1px solid var(--bg-primary)'
                }}
              >
                Meet Our Doctor
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
